import React, { useMemo } from "react";
import { useAtomValue } from "jotai";
import { Box, Typography } from "@mui/material";
import type { ChartData } from "atoms/Ltool/chartAtoms";
import { mappingAtom } from "atoms/mappingAtoms";
import { resolveChartThreeSigma } from "../utils/chartCorrection";
import { buildRiskMarginItems } from "../utils/riskMargin";

type Props = {
    allChartData: Record<string, ChartData>;
    /** 表示する現象グループ（並び順もこれに従う）。ACHIEVEMENT_GROUP_IDS を渡す。 */
    groupIds: readonly string[];
};

// 達成度を計算したときの補正値（現象グループ別、dB）。グラフは設計値 + 補正値で判定した結果と
// 揃えて描くため、どの補正値が使われたかをここで確認できるようにする。
// 値は条件（セル）ごとのレスポンスに入るので、計算済みセルが一致するときだけ値を出し、
// 食い違うときは誤読を避けるため値を出さず警告する。計算済みセルが無ければ何も出さない。
export const ChartThreeSigma: React.FC<Props> = ({ allChartData, groupIds }) => {
    const mapping = useAtomValue(mappingAtom);
    const resolution = useMemo(() => resolveChartThreeSigma(allChartData, groupIds), [allChartData, groupIds]);

    if (resolution.status === "none") return null;

    // 値の形（現象グループ別の dB）はマージンと同じため、表示項目の組み立てを共用する。
    const items =
        resolution.status === "uniform" ? buildRiskMarginItems(resolution.values, groupIds, mapping) : [];

    return (
        <Box
            role="group"
            aria-label="補正値"
            sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5 }}
        >
            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                補正値
            </Typography>
            {resolution.status === "mixed" ? (
                <Typography variant="body2" component="span" color="warning.main">
                    条件により異なります
                </Typography>
            ) : (
                items.map((item) => (
                    <Typography key={item.groupId} variant="body2" component="span" color="text.secondary">
                        {item.text}
                    </Typography>
                ))
            )}
        </Box>
    );
};
